/**
 * Earned Wage Access (EWA) self-service API client (ADR-0009 D9, Phase 3b).
 * Self-scoped to the authenticated employee — no employeeId param.
 */
import { apiRequest } from './client.js';

export interface EwaLedgerLine {
  kind: string;
  amount: string;
  effectiveDate: string;
}

export interface EwaDraw {
  id: string;
  status: string;
  amount: string;
  currency: string;
  remainingBalance: string;
  startPeriod: string;
  disbursedAt: string | null;
  createdAt: string;
  ledger: EwaLedgerLine[];
}

/** Earned-to-date + the available-to-draw ceiling (post loan-net + policy cap). */
export interface EwaQuote {
  earnedToDate: string;
  availableToDraw: string;
  currency: string;
  reservedLoanInstallment: string;
  priorDrawsThisPeriod: string;
}

export interface EwaPeriod {
  periodStart: string;
  periodEnd: string;
  asOf: string;
}

export function getEwaQuote(p: EwaPeriod): Promise<EwaQuote> {
  const qs = new URLSearchParams({ periodStart: p.periodStart, periodEnd: p.periodEnd, asOf: p.asOf }).toString();
  return apiRequest<EwaQuote>(`/v1/me/ewa-draws/quote?${qs}`);
}

export function listEwaDraws(): Promise<{ draws: EwaDraw[] }> {
  return apiRequest<{ draws: EwaDraw[] }>('/v1/me/ewa-draws');
}

export interface CreateEwaDrawResult {
  id: string;
  status: string;
  amount: string;
  currency: string;
  startPeriod: string;
  earnedToDate: string;
  availableToDraw: string;
}

export function createEwaDraw(body: { amount: string } & EwaPeriod): Promise<CreateEwaDrawResult> {
  return apiRequest<CreateEwaDrawResult>('/v1/me/ewa-draws', { method: 'POST', body });
}

/** The employee's current pay period ≈ the current calendar month (UTC). v1
 *  has no per-employee period derivation server-side, so the FE supplies it. */
export function currentPeriod(now: Date = new Date()): EwaPeriod {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const pad = (n: number) => String(n).padStart(2, '0');
  const start = `${y}-${pad(m + 1)}-01`;
  const end = new Date(Date.UTC(y, m + 1, 0)).toISOString().slice(0, 10); // last day of month
  const asOf = now.toISOString().slice(0, 10);
  return { periodStart: start, periodEnd: end, asOf };
}
